/**
 * Control Tower Page
 * Unified org health dashboard for enterprise tier
 *
 * Shows:
 * - Organization health score
 * - Team health breakdown
 * - Project coverage
 * - Standards coverage metrics
 * - Risk indicators
 * - Governance activity
 */

import React, { useState, useEffect } from 'react';
import { Card, Badge, Progress, Alert } from 'flowbite-react';
import {
  HiShieldCheck,
  HiUserGroup,
  HiFolderOpen,
  HiLightBulb,
  HiExclamation,
  HiRefresh,
  HiCheckCircle,
  HiClipboardList,
  HiChartBar,
  HiExclamationCircle,
} from 'react-icons/hi';
import {
  enterpriseApi,
  ControlTowerData,
} from '../api/enterprise';
import { useStatus } from '../contexts/StatusContext';

const ControlTower: React.FC = () => {
  const { hasFeature } = useStatus();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ControlTowerData | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = async () => {
    setError(null);
    setData(null);
    try {
      const result = await enterpriseApi.getControlTower();
      if (result.success && result.data) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to load Control Tower data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const getHealthColor = (score: number) => {
    if (score >= 70) return 'green';
    if (score >= 40) return 'yellow';
    return 'red';
  };

  const unavailable = (source: string) => data?.unavailable_sources?.includes(source) ?? false;
  const metric = (source: string, value: number | string) => unavailable(source) ? '—' : value;

  const getRiskBadgeColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'failure';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'gray';
    }
  };

  // Feature gate — Enterprise only
  if (!hasFeature('audit_trail')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <HiShieldCheck className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Enterprise Feature</h2>
        <p className="text-gray-500 text-center max-w-md">
          Control Tower is available on Enterprise plans.
          Upgrade to access organization health scoring, risk indicators, and governance dashboards.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Control Tower</h1>
          <p className="mt-1 text-sm text-gray-500">
            {data?.company_name ? `${data.company_name} — ` : ''}Organization health and governance overview
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <HiRefresh className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && <Alert color="failure">{error}</Alert>}

      {data && (
        <>
          {(data.unavailable_sources?.length ?? 0) > 0 && (
            <Alert color="warning">Some data could not be loaded: {data.unavailable_sources?.join(', ')}. Refresh to retry.</Alert>
          )}
          {/* Health Score + Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Org Health Score */}
            <Card className="border-2 border-purple-200">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-500 mb-2">Activity Score</p>
                {data.health_score != null ? (
                  <>
                    <div className="relative inline-flex items-center justify-center">
                      <div className={`text-4xl font-bold text-${getHealthColor(data.health_score)}-600`}>
                        {data.health_score}
                      </div>
                      <span className="text-lg text-gray-400 ml-1">/100</span>
                    </div>
                    <Progress progress={data.health_score} color={getHealthColor(data.health_score)} className="mt-3" />
                  </>
                ) : (
                  <p className="text-sm text-gray-600">
                    {data.health_score_status === 'unavailable' ? 'Score unavailable' : 'Not enough activity to score'}
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  Team activity, project usage, standards breadth and published knowledge. This score does not assess governance evidence.
                </p>
              </div>
            </Card>

            {/* Team Health */}
            <Card>
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100">
                  <HiUserGroup className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Team Health</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {metric('team', data.team.active)}/{metric('team', data.team.total)}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-green-600">{metric('team', data.team.active)} active</span>
                {data.team.stale > 0 && (
                  <span className="text-yellow-600">{metric('team', data.team.stale)} stale</span>
                )}
                {data.team.very_stale > 0 && (
                  <span className="text-red-600">{metric('team', data.team.very_stale)} inactive</span>
                )}
              </div>
            </Card>

            {/* Standards Coverage */}
            <Card>
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100">
                  <HiShieldCheck className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Standards Active</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {metric('standards_coverage', data.standards_coverage.unique_standards)}
                  </p>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-500">
                {metric('standards_coverage', data.standards_coverage.total_injections.toLocaleString())} injections across{' '}
                {metric('standards_coverage', data.standards_coverage.sessions_with_standards)} sessions
              </div>
            </Card>

            {/* Governance */}
            <Card>
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-purple-100">
                  <HiClipboardList className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Audit Activity</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {metric('audit', data.governance.audit_events_30d)}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-gray-600">audit events (30d)</span>
                <span className="text-purple-600">
                  {metric('knowledge', data.governance.published_knowledge)} knowledge items
                </span>
              </div>
            </Card>
          </div>

          {/* Risk Indicators */}
          {data.risks.length > 0 && (
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Indicators</h3>
              <div className="space-y-3">
                {data.risks.map((risk, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      risk.severity === 'high'
                        ? 'bg-red-50 border-red-200'
                        : risk.severity === 'medium'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {risk.severity === 'high' ? (
                        <HiExclamationCircle className="h-5 w-5 text-red-600" />
                      ) : (
                        <HiExclamation className="h-5 w-5 text-yellow-600" />
                      )}
                      <span className="text-sm font-medium">{risk.message}</span>
                    </div>
                    <Badge color={getRiskBadgeColor(risk.severity)}>
                      {risk.severity}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {data.risks.length === 0 && !unavailable('team') && !unavailable('projects') && data.health_score_status !== 'insufficient_evidence' && (
            <Card>
              <div className="text-center py-6">
                <HiCheckCircle className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <h3 className="text-lg font-semibold text-gray-900">No activity risk thresholds triggered</h3>
                <p className="text-sm text-gray-500">Based on the available team and project activity.</p>
              </div>
            </Card>
          )}

          {/* Project Coverage */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Project Coverage</h3>
              <span className="text-sm text-gray-500">
                {metric('projects', data.projects.filter(p => p.status === 'active').length)} of {metric('projects', data.projects.length)} active
              </span>
            </div>

            {data.projects.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-700 bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Project</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3 text-center">Repo</th>
                      <th className="px-4 py-3 text-right">Sessions (30d)</th>
                      <th className="px-4 py-3 text-right">Standards Used</th>
                      <th className="px-4 py-3 text-right">Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.projects.map((project) => (
                      <tr key={project.project_id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {project.project_name}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge color={project.status === 'active' ? 'success' : 'gray'}>
                            {project.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {project.repo_connected ? (
                            <HiCheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                          ) : (
                            <span className="text-gray-400">--</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {project.sessions_30d}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {project.standards_used}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500">
                          {project.last_session
                            ? new Date(project.last_session).toLocaleDateString()
                            : 'Never'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <HiFolderOpen className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p>{unavailable('projects') ? 'Project coverage unavailable' : 'No projects configured'}</p>
              </div>
            )}
          </Card>

          {/* Quick Navigation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="/audit"
              className="block p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <HiClipboardList className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <h4 className="font-semibold text-gray-900">Audit Trail</h4>
                  <p className="text-sm text-gray-500">
                    {metric('audit', data.governance.audit_events_30d)} events this month
                  </p>
                </div>
              </div>
            </a>
            <a
              href="/knowledge"
              className="block p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <HiLightBulb className="h-8 w-8 text-amber-600" />
                <div className="ml-4">
                  <h4 className="font-semibold text-gray-900">Knowledge Base</h4>
                  <p className="text-sm text-gray-500">
                    {metric('knowledge', data.governance.knowledge_items)} items ({metric('knowledge', data.governance.published_knowledge)} published)
                  </p>
                </div>
              </div>
            </a>
            <a
              href="/reports"
              className="block p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <HiChartBar className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <h4 className="font-semibold text-gray-900">Executive Reports</h4>
                  <p className="text-sm text-gray-500">Standards ROI and team analytics</p>
                </div>
              </div>
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default ControlTower;
